import React, {Children} from 'react';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {Item} from './item';
import {
    Align,
    Direction,
    Indent,
    Justify,
    DIRECTION_CLASSES,
    INDENT_CLASSES,
    ALIGN_CLASSES,
    JUSTIFY_CLASSES,
} from './constants';
import styles from './element-group.module.styl';

import type {ReactNode} from 'react';
import type {IndentType, DirectionType, AlignType, JustifyType} from './constants';


const classNames = withCssModule(styles);

/**
 * @description GroupProps
 */
export interface GroupProps {
    /**
     * The alignment of items on the Cross Axis
     */
    align?: AlignType,
    /**
     * ElementGroup is treated as block element (display: block)
     */
    block?: boolean,
    /**
     * ElementGroup children element
     */
    children?: ReactNode,
    /**
     * Use content height for ElementGroup
     */
    contentHeight?: boolean,
    /**
     * Data attributes
     */
    data?: object,
    /**
     *  Defining the main axis direction of the ElementGroup
     */
    direction?: DirectionType,
    /**
     * It enables a flex context for all its direct children.
     */
    flex?: boolean,
    /**
     * Should ElementGroup Item take all available width
     */
    full?: boolean,
    /**
     * Get ElementGroup Ref
     */
    getRef?: (element: HTMLDivElement | null) => void,
    /**
     * Indent between elements in group
     */
    indent?: IndentType,
    /**
     * ElementGroup is treated as inline element (display: inline-flex)
     */
    inline?: boolean,
    /**
     * Defines how the browser distributes space between and around Items along the main-axis
     */
    justify?: JustifyType,
    /**
     * ClassName for ElementGroup
     */
    mix?: string,
    /**
     * Revers margin in Items
     */
    revers?: boolean,
    /**
     * Flexbox wrap
     */
    wrap?: boolean,
}

/**
 * @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-group` instead
 * @see [Group]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/layout-group--documentation}
 * */

export const ElementGroup = (props: GroupProps) => {
    const {
        align = Align.CENTER,
        block = false,
        children,
        contentHeight = false,
        data,
        direction = Direction.HORIZONTAL,
        flex = false,
        full = false,
        getRef = () => undefined,
        indent = Indent.M,
        inline = false,
        mix = '',
        justify = Justify.START,
        revers = false,
        wrap = false,
    } = props;

    const classes = classNames(
        'element-group',
        mix,
        {
            'element-group_full': full,
            'element-group_inline': inline,
            'element-group_flex': flex,
            'element-group_block': block,
            'element-group_wrap': wrap,
            'element-group_revers': revers,
            'element-group_content-height': contentHeight,
            [JUSTIFY_CLASSES[justify]]: !block || flex,
            [ALIGN_CLASSES[align]]: !block || flex,
        },
        DIRECTION_CLASSES[direction],
        INDENT_CLASSES[indent],
    );

    return (
        <div ref={getRef} {...data} className={classes}>
            {Children.map(children, (child, index) => {
                let item = null;
                if (child) {
                    item = React.isValidElement(child) && child.type === Item
                        ? Children.only(child)
                        : <Item key={index}>{child}</Item>;
                }
                return item;
            })}
        </div>
    );
};

ElementGroup.displayName = 'ElementGroup';
